import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useLogger } from './LoggerProvider.tsx';

export default function ScrollProvider() {
  const logger = useLogger('scroll-provider');
  const location = useLocation();
  const positions = useRef(new Map());
  const prevLocationRef = useRef(location);

  // Extracts the "route path" without query/hash
  const getRouteKey = (loc) => {
    if (loc.hash.startsWith('#/')) {
      // HashRouter: route is after "#/"
      const routePart = loc.hash.slice(2).split('?')[0] || '/';
      return routePart;
    } else {
      // BrowserRouter: normal pathname
      return loc.pathname;
    }
  };

  // Save scroll position before navigation
  useEffect(() => {
    const savePosition = () => {
      const prevKey = prevLocationRef.current.key;
      positions.current.set(prevKey, window.scrollY);
    };

    window.addEventListener('beforeunload', savePosition);
    window.addEventListener('popstate', savePosition);

    return () => {
      window.removeEventListener('beforeunload', savePosition);
      window.removeEventListener('popstate', savePosition);
    };
  }, []);

  // Handle scroll after location changes
  useEffect(() => {
    const prevLoc = prevLocationRef.current;
    const { hash, key } = location;

    const prevRoute = getRouteKey(prevLoc);
    const currentRoute = getRouteKey(location);

    // Back/forward: restore saved scroll position
    if (positions.current.has(key)) {
      logger.debug('restoring scroll position', { key, position: positions.current.get(key) });
      window.scrollTo(0, positions.current.get(key));
    }
    // Hash anchor navigation (same route, real hash)
    else if (hash && !hash.startsWith('#/') && currentRoute === prevRoute) {
      const el = document.getElementById(hash.substring(1));
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
    // Same route (query/state change) → keep position
    else if (currentRoute === prevRoute) {
      // do nothing
    }
    // Normal route change → scroll to top
    else {
      logger.debug('scrolling to top');
      window.scrollTo(0, 0);
    }

    prevLocationRef.current = location;
  }, [location]);

  return null;
}
